import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import IntlComponent from 'axway-react-intl';
import { CodeEditor, ModalDialog, JsonSchema } from 'axway-react-ui';

class FieldEditorDialogContent extends ModalDialog.Content {
	constructor(props) {
		super(props);
		IntlComponent.init(this);
		this.onChange = this.onChange.bind(this);
		this.onBeforeChange = this.onBeforeChange.bind(this);
		this.wrapper = this.splitWrapper();
	}

	/**
	 * Get metadata for the multilineWrapper defined by the parameter options
	 * The value is cached so metadata only gets recalculated if the wrapper changed
	 * @returns {object} wrapper info including the split
	 *                   as well as the cached length of the trimmed wrapper
	 */
	splitWrapper() {
		const { multilineWrapper } = this.props;
		if (typeof multilineWrapper !== 'object') {
			return;
		}

		const { before, after } = multilineWrapper;

		if (this.wrapper && (before === this.wrapper.before
			&& after === this.wrapper.after)) {
			return this.wrapper;
		}

		return {
			before,
			after,
			beforeTrimLength: before.trim().length,
			afterTrimLength: after.trim().length
		};
	}

	componentDidUpdate() {
		// recalculate the metadata if necessary
		this.wrapper = this.splitWrapper();
	}

	onChange(value) {
		if (!this.wrapper) {
			this.props.onChange(value);
			return;
		}
		// if we have a wrapper, then "unwrap" the value to get the user provided one
		const { beforeTrimLength, afterTrimLength } = this.wrapper;
		// magic to get the substring in-between both sides of the wrapper.
		// the end result is trimmed.
		const newValue = value.substring(beforeTrimLength, value.length - afterTrimLength).trim();
		if (this.props.value !== newValue) {
			// Only call onChange if the value actually changed
			this.props.onChange(newValue);
		}
	}

	/**
	 * Called before the code editor makes a change
	 * Here we cancel the change if the user tries to edit any
	 * text which is part of the provided wrapper.
	 * Whitespace which is part of the wrapper *can* be edited, however
	 * it won't have an effect on the value that we return
	 * @param {object} cm codemirror instance
	 * @param {object} event codemirror event object
	 */
	onBeforeChange(cm, event) {
		const fromIndex = cm.indexFromPos(event.from);
		this.cancelIfInWrapper(cm, fromIndex, event, this.wrapper);
		// Check if a change was made over a range and if the event wasn't already cancelled
		if (!event.canceled
			&& (event.from.line !== event.to.line || event.from.ch !== event.to.ch)) {
			const toIndex = cm.indexFromPos(event.to);
			this.cancelIfInWrapper(cm, toIndex, event, this.wrapper);
		}
	}

	/**
	 * Cancels a change event if the provided index is within
	 * any non-editable section of the wrapper.
	 * @param {object} cm codemirror instance
	 * @param {number} index index of the change
	 * @param {object} event cm event object
	 * @param {object} wrapper wrapper metadata
	 */
	cancelIfInWrapper(cm, index, event, wrapper) {
		// Check if the index is less than the beginning of the
		// "before" part of the wrapper (i.e. in a non-editable area)
		if (index < wrapper.beforeTrimLength) {
			return event.cancel();
		}
		const currentValue = cm.getValue();
		// check if the index is greater than the beginning
		// of the "after" part of the wrapper (i.e. in a non-editable area)
		if (index > currentValue.length - wrapper.afterTrimLength) {
			event.cancel();
		}
	}

	render() {
		const { mode, name, schema, value, hideSchema } = this.props;
		let displayValue = value;
		if (this.wrapper) {
			// apply wrapper to value when displaying
			const { before, after } = this.wrapper;
			displayValue = before + (value || '') + after;
		}
		return (
			<div className="nodeconfigfield-dialogcontent">
				<div className="nodeconfigfield-fieldcode">
					<CodeEditor
						code={displayValue}
						mode={mode}
						onBeforeChange={this.wrapper && this.onBeforeChange}
						scrollbarStyle="simple"
						lint
						onChange={this.onChange}
					/>
				</div>
				{!hideSchema && (
					<div className="nodeconfigfield-fieldschema">
						<JsonSchema
							schema={schema}
							title={name}
						/>
					</div>
				)}
			</div>
		);
	}
}

FieldEditorDialogContent.propTypes = {
	name: PropTypes.string.isRequired,
	schema: PropTypes.object.isRequired,
	value: PropTypes.string,
	onChange: PropTypes.func,
	mode: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.object
	]),
	hideSchema: PropTypes.bool,
	multilineWrapper: PropTypes.shape({
		before: PropTypes.string.isRequired,
		after: PropTypes.string.isRequired
	})
};

FieldEditorDialogContent.defaultProps = {
};

export default injectIntl(FieldEditorDialogContent);
